import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as reminderService from '../services/reminder.service';
import {
  ListRemindersParams,
  CreateReminderPayload,
  UpdateReminderPayload,
} from '../types/reminder.types';
import toast from 'react-hot-toast';

export const REMINDERS_KEY = 'reminders';
export const REMINDER_STATS_KEY = 'reminder_stats';
export const REMINDER_DETAIL_KEY = 'reminder_detail';
export const REMINDER_LOGS_KEY = 'reminder_logs';

/**
 * Hook to fetch paginated reminders
 */
export const useReminders = (chatBotId: string, params?: ListRemindersParams) => {
  return useQuery({
    queryKey: [REMINDERS_KEY, chatBotId, params],
    queryFn: () => reminderService.listReminders(chatBotId, params),
    enabled: Boolean(chatBotId),
  });
};

/**
 * Hook to fetch reminder dashboard statistics
 */
export const useReminderStats = (chatBotId: string) => {
  return useQuery({
    queryKey: [REMINDER_STATS_KEY, chatBotId],
    queryFn: () => reminderService.getReminderStats(chatBotId),
    enabled: Boolean(chatBotId),
  });
};

/**
 * Hook to fetch single reminder details
 */
export const useReminderDetail = (chatBotId: string, reminderId?: string) => {
  return useQuery({
    queryKey: [REMINDER_DETAIL_KEY, chatBotId, reminderId],
    queryFn: () => reminderService.getReminderById(chatBotId, reminderId!),
    enabled: Boolean(chatBotId && reminderId),
  });
};

/**
 * Hook to fetch recipient logs for a reminder
 */
export const useReminderLogs = (
  reminderId?: string,
  params?: { page?: number; limit?: number; status?: string; search?: string }
) => {
  return useQuery({
    queryKey: [REMINDER_LOGS_KEY, reminderId, params],
    queryFn: () => reminderService.listReminderLogs(reminderId!, params),
    enabled: Boolean(reminderId),
  });
};

/**
 * Hook for reminder mutations (create, update, delete, pause, resume, retry)
 */
export const useReminderMutations = (chatBotId: string) => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [REMINDERS_KEY, chatBotId] });
    queryClient.invalidateQueries({ queryKey: [REMINDER_STATS_KEY, chatBotId] });
  };

  /* Instantly reflect status changes in the cache so the button flips before
     the server responds, preventing duplicate pause/resume requests. */
  const updateReminderInCache = (patch: { _id: string; [key: string]: any }) => {
    queryClient.setQueriesData({ queryKey: [REMINDERS_KEY, chatBotId] }, (old: any) => {
      if (!old?.reminders) return old;
      return { ...old, reminders: old.reminders.map((r: any) => (r._id === patch._id ? { ...r, ...patch } : r)) };
    });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateReminderPayload) =>
      reminderService.createReminder(chatBotId, payload),
    onSuccess: () => {
      toast.success('Reminder scheduled successfully');
      invalidateAll();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ reminderId, payload }: { reminderId: string; payload: UpdateReminderPayload }) =>
      reminderService.updateReminder(chatBotId, reminderId, payload),
    onSuccess: (_, { reminderId }) => {
      toast.success('Reminder updated successfully');
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: [REMINDER_DETAIL_KEY, chatBotId, reminderId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (reminderId: string) =>
      reminderService.deleteReminder(chatBotId, reminderId),
    onSuccess: () => {
      toast.success('Reminder deleted successfully');
      invalidateAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete reminder');
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (reminderId: string) => reminderService.pauseReminder(chatBotId, reminderId),
    onMutate: (reminderId) => updateReminderInCache({ _id: reminderId, status: 'paused' }),
    onSuccess: (updated) => {
      toast.success('Reminder paused');
      if (updated?._id) updateReminderInCache(updated);
      invalidateAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to pause reminder');
      invalidateAll();
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (reminderId: string) => reminderService.resumeReminder(chatBotId, reminderId),
    onMutate: (reminderId) => updateReminderInCache({ _id: reminderId, status: 'scheduled' }),
    onSuccess: (updated) => {
      toast.success('Reminder resumed');
      if (updated?._id) updateReminderInCache(updated);
      invalidateAll();
    },
    onError: () => {
      invalidateAll();
    },
  });

  const retryFailedMutation = useMutation({
    mutationFn: (reminderId: string) =>
      reminderService.retryFailedRecipients(chatBotId, reminderId),
    onSuccess: (res, reminderId) => {
      toast.success(res.message || 'Retry queued');
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: [REMINDER_LOGS_KEY, reminderId] });
      queryClient.invalidateQueries({ queryKey: [REMINDER_DETAIL_KEY, chatBotId, reminderId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to retry recipients');
    },
  });

  return {
    createReminder: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateReminder: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteReminder: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    pauseReminder: pauseMutation.mutateAsync,
    isPausing: pauseMutation.isPending,
    resumeReminder: resumeMutation.mutateAsync,
    isResuming: resumeMutation.isPending,
    retryFailed: retryFailedMutation.mutateAsync,
    isRetrying: retryFailedMutation.isPending,
  };
};
